import { Component, Input, OnChanges, OnInit, ViewChild } from "@angular/core";
import { VTableComponent } from "@app/directives/_layout/v-table/_components/v-table.component";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { IDateCellOptions, VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortDirection } from "@enums/SortDirection";
import { DateService, DateUnit } from "@services/date.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { ITemperatureProbe, ITemperatureProbeWithTemperatureData } from "@temperature/_models/temperature/ITemperatureProbe";
import { IVehicleWithTemperatureData } from "@temperature/_models/temperature/IVehicleWithTemperatureData";
import { TemperatureProbeService } from "@temperature/_services/temperature-probe.service";
import { TemperatureRecordService } from "@temperature/_services/temperature-record.service";
import { lastValueFrom } from "rxjs";
import './probe-detail.component.scss';

@Component({
    selector: 'probe-detail',
    template: require('./probe-detail.component.html')
})
export class ProbeDetailComponent implements OnChanges, OnInit {
    loading = false;

    @Input() vehicle: IVehicleWithTemperatureData;

    @ViewChild(VTableComponent) table: VTableComponent;

    //--
    temperatureProbes: ITemperatureProbeWithTemperatureData[];

    UnitConversionType = UnitConversionType;

    SortDirection = SortDirection;

    dateCellOptions: IDateCellOptions;

    // Tables
    tableColumns: IVTableColumn<ITemperatureProbeWithTemperatureData>[];
    tableRows: IVTableRow[];
    constructor(
        private readonly dateService: DateService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly temperatureRecordService: TemperatureRecordService,
        private readonly unitConversionService: UnitConversionService,
        private readonly vTableService: VTableService) { }


    ngOnInit(): void {
        this.createTable();
    }

    ngOnChanges(): void {
        this.getData();
    }

    //--


    private getData(): Promise<void> {
        this.loading = true;
        const promises = [];
        promises.push(this.getProbes());
        promises.push(lastValueFrom(this.temperatureRecordService.getLatestTemperatureRecordsForVehicle(this.vehicle.Id)));

        return Promise.all(promises).then(([probes, latestTemperatureRecords]) => {

            probes.forEach(probe => {
                this.temperatureProbeService.mapProbeLatestRecord(probe, latestTemperatureRecords);
            });

            this.dateCellOptions = {
                showErrorIfBeforeDate: this.dateService.addOrSubtractTime(new Date(), -30, DateUnit.Min)
            }

            this.temperatureProbes = probes;
            this.getProfileNames();
            this.getTimeInAlarm();

        }).finally(() => {
            this.filterProbes();
            this.tableRows = this.vTableService.createTableRows(this.temperatureProbes, this.tableColumns);
            this.loading = false;
        });
    }

    private getProbes(): Promise<ITemperatureProbe[]> {
        if (this.vehicle.temperatureProbes?.length) {
            return Promise.resolve(this.vehicle.temperatureProbes);
        }

        return lastValueFrom(this.temperatureProbeService.getTemperatureProbesForVehicle(this.vehicle.Id));
    }

    private getProfileNames(): void {
        if (!this.temperatureProbes) {
            return;
        }

        this.temperatureProbes.forEach(probe => {
            if (!probe.TemperatureProfile) {
                probe.profileName = "";
                return;
            }

            const lowerBoundString = this.unitConversionService.smartConvert(probe.TemperatureProfile?.LowerBound, UnitConversionType.Temperature);
            const upperBoundString = this.unitConversionService.smartConvert(probe.TemperatureProfile?.UpperBound, UnitConversionType.Temperature);

            probe.profileName = `${probe.TemperatureProfile?.Name} (${lowerBoundString} - ${upperBoundString})`;

        })
    }

    private getTimeInAlarm(): void {
        this.temperatureProbes.forEach(probe => {

            const outOfBounds = probe.outOfBoundsHistories.find(history => !history.EndUtc);
            if (!outOfBounds) {
                probe.status = VTableRowStatus.Info;
                return;
            }

            if (this.temperatureProbeService.temperatureIsInBoundsAccordingToProfile(probe)) {
                return;
            }

            probe.timeInAlarm = new Date().getTime() - new Date(outOfBounds.StartUtc).getTime();
            probe.status = VTableRowStatus.Error;
            probe.inAlarm = true;
        })
    }
    
    private filterProbes(): void {
        if (!this.temperatureProbes) {
            return;
        }
        this.temperatureProbes = this.temperatureProbes.filter(probe => !!probe.latestRecord);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader(),
                createCellFunction: (probe) => this.vTableService.createStatusCell(probe.status)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TempCompliance.ProbeName" }),
                createCellFunction: (probe) => this.vTableService.createTableCell(probe.cleanName)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.LastUpdated" }),
                createCellFunction: (probe) => this.vTableService.createDateCell(
                    probe.latestRecord.Timestamp, {
                    showRelativeTime: true,
                    showErrorIfBeforeDate: this.dateService.addOrSubtractTime(new Date(), -30, DateUnit.Min),
                    showWarningIfBeforeDate: this.dateService.addOrSubtractTime(new Date(), -14, DateUnit.Min)
                })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TempCompliance.LatestTemperature" }),
                createCellFunction: (probe) => this.vTableService.createTableCell(this.unitConversionService.smartConvert(probe.latestRecord.Temperature, UnitConversionType.Temperature, { precision: 1 }), { sortData: probe.latestRecord.Temperature, classes: probe.inAlarm ? 'red-color ' : '' })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TempCompliance.TimeInAlarm" }),
                createCellFunction: (probe) => this.vTableService.createDurationCell(probe.timeInAlarm, DateUnit.Ms)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TempCompliance.Profile" }),
                createCellFunction: (probe) => this.vTableService.createTableCell(probe.profileName)
            }

        ];
    }

}
